import type {
  CommandItem,
  ExecuteCommandInput,
  NotRightReason,
} from "@ceo-cmd/shared";
import { currentTodayHref } from "@/lib/today-position";

export const RETURN_CONDITIONS = [
  "When completed",
  "When overdue",
  "If rejected",
  "If material information changes",
] as const;

export type ReturnCondition = (typeof RETURN_CONDITIONS)[number];

export const NOT_RIGHT_REASONS: Array<{ id: NotRightReason; label: string }> = [
  { id: "already_handled", label: "Already handled" },
  { id: "wrong_recommendation", label: "Wrong recommendation" },
  { id: "incorrect_information", label: "Incorrect information" },
  { id: "not_important_enough", label: "Not important enough" },
  { id: "wrong_timing", label: "Wrong timing" },
  { id: "wrong_owner", label: "Wrong owner" },
  { id: "ceo_not_required", label: "CEO not required" },
];

export function askAiHref(item: CommandItem): string {
  const params = new URLSearchParams({
    commandItemId: item.id,
    portfolioId: item.portfolioId,
    companyId: item.companyId,
    companyName: item.companyName,
    title: item.title,
    recommendation: item.recommendation,
    situationSummary: item.situationSummary,
    whyNow: item.whyInToday,
    expectedOutcome: item.expectedOutcome,
    riskOfInaction: item.riskOfInaction,
    relevantContact: [item.contactName, item.contactTitle]
      .filter(Boolean)
      .join(" · "),
    resumeTask: "1",
    returnTo: currentTodayHref(),
  });
  return `/ai-assistant?${params.toString()}`;
}

export function laterRemindAt(
  option: "today" | "tomorrow" | "next_business_day" | "date",
  dateValue?: string,
): string {
  const now = new Date();
  if (option === "today") {
    const d = new Date(now);
    d.setHours(18, 0, 0, 0);
    if (d.getTime() <= now.getTime()) {
      d.setTime(now.getTime() + 3 * 60 * 60 * 1000);
    }
    return d.toISOString();
  }
  if (option === "tomorrow") {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  if (option === "next_business_day") {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    return d.toISOString();
  }
  if (dateValue) {
    const d = new Date(dateValue);
    if (!Number.isNaN(d.getTime())) {
      d.setHours(9, 0, 0, 0);
      return d.toISOString();
    }
  }
  return laterRemindAt("tomorrow");
}

export function buildDelegateInput(opts: {
  assignee: string;
  instruction: string;
  dueDate?: string;
  returnCondition: ReturnCondition;
}): ExecuteCommandInput {
  const expectedCompletionAt = opts.dueDate
    ? new Date(`${opts.dueDate}T17:00:00`).toISOString()
    : undefined;
  return {
    disposition: "delegate",
    delegateToName: opts.assignee.trim() || "Omar",
    delegateInstruction:
      opts.instruction.trim() ||
      "Confirm next step and update me by the due date.",
    expectedCompletionAt,
    returnToCeo: true,
    note: `Return condition: ${opts.returnCondition}`,
  };
}

export type SideEffectStatus =
  | "simulated"
  | "failed"
  | "ok"
  | "skipped_disconnected";

export type ClassifiedSideEffect = {
  label: string;
  status: SideEffectStatus;
};

/** Prefer structured appliedEffects; fall back to string classification. */
export function classifyAppliedEffects(
  applied: import("@ceo-cmd/shared").ExternalWriteRecord[] | undefined,
): ClassifiedSideEffect[] | null {
  if (!applied || applied.length === 0) return applied ? [] : null;
  return applied.map((w) => {
    const label = w.description || w.message;
    if (w.outcome === "skipped_disconnected") {
      return { label, status: "skipped_disconnected" as const };
    }
    if (w.outcome === "failed") {
      return { label, status: "failed" as const };
    }
    if (w.simulated) {
      return { label, status: "simulated" as const };
    }
    return { label, status: "ok" as const };
  });
}

/** Classify side-effect strings from execute results — honesty over theater. */
export function classifySideEffects(
  item: CommandItem,
  applied: string[],
): ClassifiedSideEffect[] {
  const fromApplied = applied
    .filter((raw) =>
      /simulat|fail|could not|unable|error|not connected|HubSpot note|skipped|Retry:/i.test(
        raw,
      ),
    )
    .map((raw) => {
      const label = raw
        .replace(/\s*\(simulated\)/gi, "")
        .replace(/\s*—\s*could not sync\s*\(simulated failure\)/gi, "")
        .replace(/^simulated[:\s]+/i, "")
        .replace(/^Retry:\s*/i, "")
        .trim();
      if (/not connected|skipped_disconnected/i.test(raw)) {
        return { label, status: "skipped_disconnected" as const };
      }
      if (/fail|could not|unable|error/i.test(raw)) {
        return { label, status: "failed" as const };
      }
      if (/simulat/i.test(raw)) {
        return { label, status: "simulated" as const };
      }
      return { label, status: "ok" as const };
    });

  if (fromApplied.length > 0) return fromApplied;

  // Local-complete: no external effects claimed
  if (applied.every((s) => !/hubspot|apollo|instantly|crm/i.test(s))) {
    return [];
  }

  return item.actionDefinition.externalSystemWrites.map((w) => ({
    label: w.description || `${w.system} update`,
    status: "simulated" as const,
  }));
}

export function personLine(item: CommandItem): string {
  const contact = [item.contactName, item.contactTitle]
    .filter(Boolean)
    .join(" · ");
  if (contact) return contact;
  if (item.ownerName) return `Owned by ${item.ownerName}`;
  if (item.domain === "FINANCE") return "Owned by Finance";
  return "Owner assigned in the source team";
}
